import { useParams, Link } from "react-router-dom";
import { Compass, BookMarked, History, MessageSquare, Sparkles, Bot, Workflow, FileQuestion } from "lucide-react";
import ComingSoonPage from "@/components/common/ComingSoonPage";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusPill } from "@/components/common/StatusPill";
import { Skeleton } from "@/components/ui/skeleton";
import { useCatalogue } from "@/context/CatalogueContext";
import { teamColour } from "@/lib/constants";

export const FindAgent = () => (
  <ComingSoonPage testid="find-agent-page" icon={Compass} title="Find an agent" description="Search the full catalogue by name, job, use case or form field." emptyTitle="The catalogue browser is on its way" emptyBody="Search, filters and agent cards arrive in the next build pass. Use Home to see what is live today." />
);

export const PromptHub = () => (
  <ComingSoonPage testid="prompt-hub-page" icon={BookMarked} title="Prompt Hub" description="Prompts that worked, shared across the team." emptyTitle="No prompts yet for this agent. Save the first one that works." emptyBody="Saving, voting and tidying prompts arrive in a later build pass." />
);

export const MyRuns = () => (
  <ComingSoonPage testid="my-runs-page" icon={History} title="My runs" description="Every run you start, newest first. Only you can see this list." emptyTitle="No runs yet" emptyBody="Open a live agent or workflow to start your first run. It will appear here." />
);

export const Feedback = () => (
  <ComingSoonPage testid="feedback-page" icon={MessageSquare} title="Feedback" description="Tell us what works, what breaks and what worries you." emptyTitle="You have not sent any feedback yet" emptyBody="The feedback form and your feedback history arrive in a later build pass." />
);

export const WhatsNew = () => (
  <ComingSoonPage testid="whats-new-page" icon={Sparkles} title="What's new" description="Announcements from the hub owner." emptyTitle="No announcements yet" emptyBody="When an Owner posts an update, it will appear here." />
);

const NotFoundEntry = ({ label, testid }) => (
  <EmptyState icon={FileQuestion} title={`We could not find that ${label}`} body="It may have been renamed or removed from the catalogue." action={<Link to="/" className="text-sm font-medium underline underline-offset-4">Back to Home</Link>} testid={testid} />
);

const EntryHeader = ({ team, title, subtitle, live, kind }) => (
  <div className="mb-8 border-l-4 pl-5" style={{ borderColor: teamColour(team, team?._index) }}>
    <p className="text-sm text-muted-foreground">{team ? team.section || team.name : "No team"} · {kind}</p>
    <h1 className="mt-1 text-xl font-semibold" data-testid="entry-title">{title}</h1>
    {subtitle && <p className="mt-2 max-w-2xl text-sm text-muted-foreground md:text-base">{subtitle}</p>}
    <div className="mt-3"><StatusPill status={live ? "live" : "soon"} /></div>
  </div>
);

export const TeamPage = () => {
  const { teamId } = useParams();
  const { teamsById, loading } = useCatalogue();
  const team = teamsById[teamId];
  if (loading) return <Skeleton className="h-32 rounded-xl" />;
  if (!team) return <NotFoundEntry label="team" testid="team-not-found" />;
  return (
    <div data-testid="team-page">
      <div className="mb-8 border-l-4 pl-5" style={{ borderColor: teamColour(team, team._index) }}>
        <p className="text-sm text-muted-foreground">Team {team.published ? "" : "· Coming soon"}</p>
        <h1 className="mt-1 text-xl font-semibold" data-testid="team-title">{team.section || team.name}</h1>
        {team.purpose && <p className="mt-2 max-w-2xl text-sm text-muted-foreground md:text-base" data-testid="team-purpose">{team.purpose}</p>}
        {team.lead && <p className="mt-2 text-sm">Led by <span className="font-medium">{team.lead}</span></p>}
      </div>
      <EmptyState icon={Bot} title="Agent cards arrive in the next pass" body="This page will list the team's agents and workflows in display order." testid="team-page-empty" />
    </div>
  );
};

export const AgentPage = () => {
  const { empNo } = useParams();
  const { agentsByKey, teamsById, loading } = useCatalogue();
  const agent = agentsByKey[empNo];
  if (loading) return <Skeleton className="h-32 rounded-xl" />;
  if (!agent) return <NotFoundEntry label="agent" testid="agent-not-found" />;
  return (
    <div data-testid="agent-page">
      <EntryHeader team={teamsById[agent.team]} title={agent.chosen_name || agent.working_name} subtitle={agent.function} live={agent.live} kind="Agent" />
      <EmptyState icon={Bot} title="The run surface arrives in the next pass" body="This page will show what the agent needs, what you get and the MindPal run surface." testid="agent-page-empty" />
    </div>
  );
};

export const WorkflowPage = () => {
  const { wid } = useParams();
  const { workflowsByKey, teamsById, loading } = useCatalogue();
  const wf = workflowsByKey[wid];
  if (loading) return <Skeleton className="h-32 rounded-xl" />;
  if (!wf) return <NotFoundEntry label="workflow" testid="workflow-not-found" />;
  return (
    <div data-testid="workflow-page">
      <EntryHeader team={teamsById[wf.team]} title={wf.name} subtitle={wf.one_line} live={wf.live} kind="Workflow" />
      <EmptyState icon={Workflow} title="The run surface arrives in the next pass" body="This page will show the approval gates, the agents involved and the MindPal run surface." testid="workflow-page-empty" />
    </div>
  );
};

export const NotFound = () => (
  <div data-testid="not-found-page">
    <PageHeader title="Page not found" description="That address does not match anything in the hub." />
    <Link to="/" className="text-sm font-medium underline underline-offset-4" data-testid="not-found-home-link">Back to Home</Link>
  </div>
);
